import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { Loader2, Plus, Download, Coins, Trash2, Pencil, Database, FileText, History, FileDown, Sparkles, Printer } from 'lucide-react'
import { InvestmentModal, DistributionModal, FxModal, ImportModal, ValuationModal, DocumentsModal } from '@/components/private/PrivateModals'
import { VintageChart, DispersionChart, CashflowChart, JCurveChart, SectorPie } from '@/components/private/PrivateCharts'
import { CommitmentsTab, KpisTab, DealsTab, FundModelTab, WaterfallTab, PmeTab } from '@/components/private/PrivateP2'

const CCY_SYMBOL: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', CHF: 'CHF ' }
const money = (v: number | null | undefined, ccy: string) =>
  v == null ? '—' : `${CCY_SYMBOL[ccy] || ''}${Math.round(v).toLocaleString()}`
const mult = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(2)}x`)
const pct = (v: number | null | undefined) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`)
const good = (ok: boolean) => (ok ? 'text-emerald-600' : 'text-red-500')

// 17 tabs in one row wrapped onto two lines and read as an undifferentiated wall.
// Grouping them by job keeps one row of groups, with the tabs for the active group
// underneath.
const TAB_GROUPS: { group: string; tabs: string[] }[] = [
  { group: 'Holdings', tabs: ['Positions', 'Distributions', 'Cash-flow timeline'] },
  { group: 'Breakdowns', tabs: ['By company', 'By sector', 'By vehicle', 'By stage', 'Exposure & concentration'] },
  { group: 'Performance', tabs: ['Vintage analysis', 'Performance attribution', 'Return dispersion', 'Benchmark (PME)'] },
  { group: 'Fund', tabs: ['Commitments & Calls', 'Waterfall', 'Fund Model'] },
  { group: 'Pipeline', tabs: ['Company KPIs', 'Deal Flow'] },
]
const P2_TABS = ['Commitments & Calls', 'Company KPIs', 'Deal Flow', 'Fund Model', 'Waterfall', 'Benchmark (PME)']
const groupOfTab = (t: string) => TAB_GROUPS.find(g => g.tabs.includes(t))?.group || TAB_GROUPS[0].group

export default function PrivateMarkets() {
  const qc = useQueryClient()
  const [ccy, setCcy] = useState('USD')
  const [tab, setTab] = useState<string>('Positions')
  const [pid, setPid] = useState<string>('')
  const [modal, setModal] = useState<null | 'invest' | 'dist' | 'fx' | 'import' | 'valuation' | 'documents'>(null)
  const [editing, setEditing] = useState<any>(null)
  const [selected, setSelected] = useState<any>(null)

  const portfoliosQ = useQuery({
    queryKey: ['pa-portfolios'],
    queryFn: () => api.get('/private/portfolios').then(r => r.data.portfolios),
  })
  const portfolios = portfoliosQ.data || []
  const activePid = pid || portfolios[0]?.id || ''

  const reportQ = useQuery({
    queryKey: ['pa-report', activePid, ccy],
    queryFn: () => api.get(`/private/portfolios/${activePid}/report`, { params: { report_ccy: ccy } }).then(r => r.data),
    enabled: !!activePid,
  })
  const distQ = useQuery({
    queryKey: ['pa-dist', activePid],
    queryFn: () => api.get(`/private/portfolios/${activePid}/distributions`).then(r => r.data.distributions),
    enabled: !!activePid,
  })
  const rep = reportQ.data

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['pa-report'] })
    qc.invalidateQueries({ queryKey: ['pa-dist'] })
  }
  const seedDemo = async () => { await api.post(`/private/portfolios/${activePid}/seed-demo`); refresh() }
  const exportXlsx = async () => {
    const r = await api.get(`/private/portfolios/${activePid}/export.xlsx`, { params: { report_ccy: ccy }, responseType: 'blob' })
    const url = URL.createObjectURL(r.data); const a = document.createElement('a')
    a.href = url; a.download = `private-markets-${ccy}.xlsx`; a.click(); URL.revokeObjectURL(url)
  }
  const exportPdf = async () => {
    const r = await api.get(`/private/portfolios/${activePid}/report.pdf`, { params: { report_ccy: ccy }, responseType: 'blob' })
    const url = URL.createObjectURL(r.data); const a = document.createElement('a')
    a.href = url; a.download = `private-markets-${ccy}.pdf`; a.click(); URL.revokeObjectURL(url)
  }
  const [insights, setInsights] = useState<{ loading: boolean; text?: string; error?: string } | null>(null)
  const runInsights = async () => {
    setInsights({ loading: true })
    try {
      const r = await api.post(`/private/portfolios/${activePid}/insights`, { report_ccy: ccy })
      setInsights(r.data.available ? { loading: false, text: r.data.insights } : { loading: false, error: r.data.reason })
    } catch (e: any) { setInsights({ loading: false, error: e?.response?.data?.detail || 'Failed' }) }
  }
  const delInvestment = async (id: string) => { if (confirm('Delete this investment?')) { await api.delete(`/private/investments/${id}`); refresh() } }
  const delDist = async (id: string) => { if (confirm('Delete this distribution?')) { await api.delete(`/private/distributions/${id}`); refresh() } }

  if (portfoliosQ.isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-gray-400" /></div>

  return (
    <div className="space-y-4 p-1">
      {/* header / toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Private Markets</h1>
          <p className="text-sm text-gray-500">
            {rep ? `${rep.counts.positions} positions · ${rep.counts.distributions} distributions` : 'Direct / angel / VC-PE portfolio'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select className="rounded-md border border-gray-300 px-2 py-1.5 text-sm" value={activePid} onChange={e => setPid(e.target.value)}>
            {portfolios.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div className="flex overflow-hidden rounded-md border border-gray-300">
            {['USD', 'EUR', 'GBP', 'CHF'].map(c => (
              <button key={c} onClick={() => setCcy(c)} className={`px-2.5 py-1.5 text-sm ${ccy === c ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{c}</button>
            ))}
          </div>
          <button className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={() => setModal('fx')}><Coins size={15} />FX</button>
          <button className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={() => setModal('import')}><Download size={15} />Import</button>
          <button className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={exportXlsx}><FileDown size={15} />Export</button>
          <button className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={exportPdf}><Printer size={15} />PDF</button>
          <button className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={runInsights}><Sparkles size={15} />AI insights</button>
          <button className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={() => { setEditing(null); setModal('dist') }}><Plus size={15} />Record distribution</button>
          <button className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700" onClick={() => { setEditing(null); setModal('invest') }}><Plus size={15} />Add investment</button>
        </div>
      </div>

      {/* KPI cards (4 currencies) */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {rep && ['GBP', 'USD', 'EUR', 'CHF'].map(c => {
          const k = rep.kpis[c]
          return (
            <div key={c} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="text-xs text-gray-400">TVPI · {c}</div>
              <div className="mt-1 rounded bg-emerald-50 px-2 py-1 text-2xl font-bold text-emerald-700">{mult(k?.tvpi)}</div>
              <div className="mt-2 space-y-0.5 text-xs text-gray-500">
                <div>MOIC {mult(k?.tvpi)} · real {mult(k?.dpi)} / unreal {mult(k?.rvpi)}</div>
                <div>Paid-in {money(k?.paid_in, c)} · Distributed {money(k?.distributed, c)}</div>
                <div>Residual {money(k?.residual, c)} · XIRR {pct(k?.xirr)}</div>
              </div>
            </div>
          )
        })}
      </div>

      {/* empty state */}
      {rep && rep.counts.positions === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center">
          <p className="text-gray-600">No positions yet.</p>
          <div className="mt-3 flex justify-center gap-2">
            <button className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white" onClick={() => setModal('invest')}><Plus size={15} />Add your first investment</button>
            <button className="flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm" onClick={seedDemo}><Database size={15} />Load demo data</button>
          </div>
        </div>
      )}

      {/* tabs — groups on top, the active group's tabs underneath */}
      <div>
        <div className="flex flex-wrap gap-x-6 border-b border-gray-200 text-sm">
          {TAB_GROUPS.map(g => {
            const isActive = groupOfTab(tab) === g.group
            return (
              <button
                key={g.group}
                onClick={() => setTab(g.tabs[0])}
                className={`-mb-px border-b-2 pb-2 pt-1 ${isActive ? 'border-blue-600 font-medium text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
              >
                {g.group}
                <span className="ml-1 text-xs text-gray-400">{g.tabs.length}</span>
              </button>
            )
          })}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {(TAB_GROUPS.find(g => g.group === groupOfTab(tab))?.tabs || []).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-full px-3 py-1 text-xs ${tab === t ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {reportQ.isFetching && <div className="text-xs text-gray-400">Updating…</div>}

      {rep && (rep.counts.positions > 0 || P2_TABS.includes(tab)) && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          {tab === 'Commitments & Calls' && <CommitmentsTab portfolioId={activePid} ccy={ccy} />}
          {tab === 'Company KPIs' && <KpisTab positions={rep.positions} />}
          {tab === 'Deal Flow' && <DealsTab portfolioId={activePid} />}
          {tab === 'Fund Model' && <FundModelTab />}
          {tab === 'Waterfall' && <WaterfallTab portfolioId={activePid} ccy={ccy} />}
          {tab === 'Benchmark (PME)' && <PmeTab portfolioId={activePid} ccy={ccy} />}
          {tab === 'Positions' && <PositionsTable rows={rep.positions} ccy={ccy} onEdit={(r: any) => { setEditing(r); setModal('invest') }} onDelete={delInvestment} onValuation={(r: any) => { setSelected(r); setModal('valuation') }} onDocs={(r: any) => { setSelected(r); setModal('documents') }} />}
          {tab === 'By company' && <GroupTable rows={rep.by_company} ccy={ccy} label="Company" />}
          {tab === 'By vehicle' && <GroupTable rows={rep.by_vehicle} ccy={ccy} label="Vehicle" />}
          {tab === 'By stage' && <GroupTable rows={rep.by_stage} ccy={ccy} label="Stage" />}
          {tab === 'By sector' && (
            <div className="grid gap-4 lg:grid-cols-2">
              <GroupTable rows={rep.by_sector} ccy={ccy} label="Sector" />
              <SectorPie data={rep.by_sector} />
            </div>
          )}
          {tab === 'Exposure & concentration' && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4">
                <div className="rounded-md border border-gray-200 px-4 py-2 text-sm">
                  <span className="text-gray-500">Concentration (HHI): </span>
                  <span className="font-semibold">{rep.concentration?.hhi ?? '—'}</span>
                  <span className={`ml-2 rounded px-1.5 py-0.5 text-xs ${rep.concentration?.level === 'high' ? 'bg-red-100 text-red-700' : rep.concentration?.level === 'moderate' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{rep.concentration?.level}</span>
                  <span className="ml-2 text-gray-400">≈ {rep.concentration?.n_effective} effective names</span>
                </div>
                {rep.follow_on_split?.map((g: any) => (
                  <div key={g.group} className="rounded-md border border-gray-200 px-4 py-2 text-sm">
                    <span className="text-gray-500">{g.group}: </span>
                    <span className="font-semibold">{g.entries}</span>
                    <span className="ml-2 text-gray-400">TVPI {mult(g.tvpi)} · Paid-in {money(g.paid_in, ccy)}</span>
                  </div>
                ))}
              </div>
              <ExposureTable rows={rep.exposure} ccy={ccy} />
            </div>
          )}
          {tab === 'Performance attribution' && <AttributionTable rows={rep.attribution} ccy={ccy} />}
          {tab === 'Cash-flow timeline' && (
            <div className="space-y-6">
              <div><div className="mb-1 text-sm font-medium text-gray-700">Cash flows by quarter</div><CashflowChart data={rep.cashflow} ccy={ccy} /></div>
              <div><div className="mb-1 text-sm font-medium text-gray-700">J-curve (cumulative net cash flow)</div><JCurveChart data={rep.jcurve} ccy={ccy} /></div>
            </div>
          )}
          {tab === 'Vintage analysis' && <VintageChart data={rep.vintages} ccy={ccy} />}
          {tab === 'Return dispersion' && (
            <div>
              <p className="mb-3 text-xs text-gray-500">Each bubble is a consolidated company. X = TVPI, Y = annualized XIRR, size = paid-in ({ccy}). Green ≥ 1.00x TVPI, red below.</p>
              <DispersionChart data={rep.dispersion} />
            </div>
          )}
          {tab === 'Distributions' && <DistributionsTable rows={distQ.data || []} onDelete={delDist} />}
        </div>
      )}

      {/* modals */}
      {modal === 'invest' && <InvestmentModal portfolioId={activePid} existing={editing} onClose={() => setModal(null)} onSaved={refresh} />}
      {modal === 'dist' && <DistributionModal positions={rep?.positions || []} onClose={() => setModal(null)} onSaved={refresh} />}
      {modal === 'fx' && <FxModal portfolioId={activePid} current={rep?.fx_active || {}} onClose={() => setModal(null)} onSaved={refresh} />}
      {modal === 'import' && <ImportModal portfolioId={activePid} onClose={() => setModal(null)} onSaved={refresh} />}
      {modal === 'valuation' && selected && <ValuationModal investment={selected} onClose={() => setModal(null)} onSaved={refresh} />}
      {modal === 'documents' && selected && <DocumentsModal investment={selected} onClose={() => setModal(null)} />}

      {insights && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setInsights(null)}>
          <div className="mt-12 w-full max-w-2xl rounded-lg bg-white shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900"><Sparkles size={18} />AI portfolio insights</h3>
              <button onClick={() => setInsights(null)} className="rounded p-1 text-gray-400 hover:bg-gray-100">✕</button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-5 py-4 text-sm">
              {insights.loading && <div className="flex items-center gap-2 text-gray-500"><Loader2 className="animate-spin" size={16} />Analyzing…</div>}
              {insights.error && <div className="rounded bg-amber-50 px-3 py-2 text-amber-700">{insights.error}</div>}
              {insights.text && <div className="whitespace-pre-wrap leading-relaxed text-gray-700">{insights.text}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const th = 'px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap'
const td = 'px-3 py-2 text-sm text-gray-800 whitespace-nowrap'

function PositionsTable({ rows, ccy, onEdit, onDelete, onValuation, onDocs }: any) {
  const ownPct = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(1)}%`)
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead><tr>
          {['Company', 'Sector', 'Stage', 'Vehicle', 'Instrument', 'Own %', 'Dilution', 'Paid-in', 'Distributed', 'Residual', 'DPI', 'RVPI', 'TVPI (MOIC)', 'XIRR', ''].map(h => <th key={h} className={th}>{h}</th>)}
        </tr></thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r: any) => (
            <tr key={r.id} className="hover:bg-gray-50">
              <td className={`${td} font-medium`}>{r.startup_name}{r.is_follow_on && <span className="ml-1 rounded bg-blue-50 px-1 text-[10px] text-blue-600">FO</span>}</td>
              <td className={td}>{r.sector}</td>
              <td className={td}>{r.stage}</td><td className={td}>{r.vehicle}</td>
              <td className={td}>{r.cost_instrument_type}</td>
              <td className={td}>{ownPct(r.cur_ownership_pct)}</td>
              <td className={`${td} ${(r.dilution_pct ?? 0) > 0 ? 'text-red-500' : ''}`}>{r.dilution_pct == null ? '—' : `${r.dilution_pct > 0 ? '−' : ''}${Math.abs(r.dilution_pct).toFixed(1)}pp`}</td>
              <td className={td}>{money(r.paid_in, ccy)}</td>
              <td className={td}>{money(r.distributed, ccy)}</td>
              <td className={td}>{money(r.residual, ccy)}</td>
              <td className={td}>{mult(r.dpi)}</td><td className={td}>{mult(r.rvpi)}</td>
              <td className={`${td} font-medium ${good((r.tvpi ?? 0) >= 1)}`}>{mult(r.tvpi)}</td>
              <td className={td}>{pct(r.xirr)}</td>
              <td className={td}>
                <div className="flex gap-1">
                  <button title="Valuation history" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-blue-600" onClick={() => onValuation(r)}><History size={14} /></button>
                  <button title="Documents" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-blue-600" onClick={() => onDocs(r)}><FileText size={14} /></button>
                  <button title="Edit" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-blue-600" onClick={() => onEdit(r)}><Pencil size={14} /></button>
                  <button title="Delete" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600" onClick={() => onDelete(r.id)}><Trash2 size={14} /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function GroupTable({ rows, ccy, label }: any) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead><tr>{[label, 'Positions', 'Paid-in', 'Distributed', 'Residual', 'DPI', 'RVPI', 'TVPI', 'XIRR'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r: any) => (
            <tr key={r.group} className="hover:bg-gray-50">
              <td className={`${td} font-medium`}>{r.group}</td>
              <td className={td}>{r.entries}</td>
              <td className={td}>{money(r.paid_in, ccy)}</td>
              <td className={td}>{money(r.distributed, ccy)}</td>
              <td className={td}>{money(r.residual, ccy)}</td>
              <td className={td}>{mult(r.dpi)}</td><td className={td}>{mult(r.rvpi)}</td>
              <td className={`${td} font-medium ${good((r.tvpi ?? 0) >= 1)}`}>{mult(r.tvpi)}</td>
              <td className={td}>{pct(r.xirr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ExposureTable({ rows, ccy }: any) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead><tr>{['Company', 'Entries', 'Paid-in', 'Paid-in %', 'Residual', 'Residual %', 'Total', 'Portfolio %', 'DPI', 'TVPI'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r: any) => (
            <tr key={r.company} className="hover:bg-gray-50">
              <td className={`${td} font-medium`}>{r.company}</td>
              <td className={td}>{r.entries}</td>
              <td className={td}>{money(r.paid_in, ccy)}</td>
              <td className={td}>{r.paid_in_pct?.toFixed(1)}%</td>
              <td className={td}>{money(r.residual, ccy)}</td>
              <td className={td}>{r.residual_pct?.toFixed(1)}%</td>
              <td className={td}>{money(r.total_value, ccy)}</td>
              <td className={td}>{r.portfolio_pct?.toFixed(1)}%</td>
              <td className={td}>{mult(r.dpi)}</td>
              <td className={`${td} font-medium ${good((r.tvpi ?? 0) >= 1)}`}>{mult(r.tvpi)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AttributionTable({ rows, ccy }: any) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead><tr>{['Company', 'Paid-in', 'Distributed', 'Residual', 'Total', 'Net value created', 'Contribution', 'DPI', 'TVPI', 'XIRR'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r: any) => (
            <tr key={r.id} className={`hover:bg-gray-50 ${(r.net_value_created ?? 0) < 0 ? 'bg-red-50/40' : ''}`}>
              <td className={`${td} font-medium`}>{r.startup_name}</td>
              <td className={td}>{money(r.paid_in, ccy)}</td>
              <td className={td}>{money(r.distributed, ccy)}</td>
              <td className={td}>{money(r.residual, ccy)}</td>
              <td className={td}>{money(r.total_value, ccy)}</td>
              <td className={`${td} ${good((r.net_value_created ?? 0) >= 0)}`}>{money(r.net_value_created, ccy)}</td>
              <td className={td}>{r.contribution_pct == null ? '—' : `${r.contribution_pct.toFixed(1)}%`}</td>
              <td className={td}>{mult(r.dpi)}</td>
              <td className={`${td} font-medium ${good((r.tvpi ?? 0) >= 1)}`}>{mult(r.tvpi)}</td>
              <td className={td}>{pct(r.xirr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function DistributionsTable({ rows, onDelete }: any) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead><tr>{['Startup', 'Stage', 'Vehicle', 'Lead', 'Date', 'Type', 'Ccy', 'Cash proceeds', ''].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r: any) => (
            <tr key={r.id} className="hover:bg-gray-50">
              <td className={`${td} font-medium`}>{r.startup_name}</td>
              <td className={td}>{r.stage}</td><td className={td}>{r.vehicle}</td><td className={td}>{r.lead || '—'}</td>
              <td className={td}>{r.event_date}</td>
              <td className={td}>{String(r.type).replace('_', ' ')}</td>
              <td className={td}>{r.denomination_ccy}</td>
              <td className={td}>{Number(r.cash_proceeds).toLocaleString()}</td>
              <td className={td}><button className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600" onClick={() => onDelete(r.id)}><Trash2 size={14} /></button></td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-gray-400">No distributions recorded.</td></tr>}
        </tbody>
      </table>
    </div>
  )
}
